import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { paymentTransactionRepository } from "./payment-transaction.repository";
import { PaymentTransaction } from "./types/payment-transaction.entity";

export type PaymentTransactionsFilter = {
  transactionId?: string;
  userId?: string;
  paymentStatus?: PaymentTransaction["paymentStatus"];
  PaymentService?: PaymentTransaction["PaymentService"];
  metaOriginalTransactionId?: string; // maps to Firestore field: meta.originalTransactionId
  subscriptionDocId?: string; // direct denormalized reference to user_subscriptions doc id
};

const paymentTransactionsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentTransactions: builder.query<(PaymentTransaction & { id: string })[], { where?: PaymentTransactionsFilter; offset?: string }>({
      queryFn: async ({ where = {}, offset }) => {
        // Map typed filters to Firestore where object
        const { metaOriginalTransactionId, subscriptionDocId, ...rest } = where as any;
        const whereFirestore: Record<string, any> = { ...rest };
        if (metaOriginalTransactionId) {
          whereFirestore["meta.originalTransactionId"] = metaOriginalTransactionId;
        }
        if (subscriptionDocId) {
          whereFirestore["subscriptionDocId"] = subscriptionDocId;
        }
        // Helper to check if a transaction matches non-id filters
        const matchesFilters = (t: PaymentTransaction, filters: Partial<PaymentTransactionsFilter>): boolean => {
          const { transactionId: _ignored, ...rest } = filters;
          const entries = Object.entries(rest) as [keyof typeof rest, any][];
          return entries.every(([key, value]) => (value == null ? true : (t as any)[key] === value));
        };

        if (where.transactionId) {
          const { transactionId, ...restFilters } = where;
          let transaction: (PaymentTransaction & { id: string }) | null = null;
          try {
            transaction = (await paymentTransactionRepository.findById(transactionId as string)) as (PaymentTransaction & { id: string }) | null;
          } catch (e) {
            return { error: (e as Error).message || "Failed to fetch payment transaction" } as any;
          }
          if (transaction && matchesFilters(transaction, restFilters)) return { data: [transaction] } as any;
          // Not found or does not match other filters: empty dataset
          return { data: [] } as any;
        }

        const res = await executeQuery(() =>
          paymentTransactionRepository.find({ where: whereFirestore, offset, orderBy: ["createdAt", "desc"] })
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: (res.data as unknown) as (PaymentTransaction & { id: string })[] };
      },
    }),

    getPaymentTransactionById: builder.query<PaymentTransaction & { id: string }, string>({
      queryFn: async (id) => {
        const res = await executeQuery(() => paymentTransactionRepository.findById(id));
        if ("error" in res) return { error: res.error } as any;
        return { data: (res.data as unknown) as PaymentTransaction & { id: string } };
      },
    }),

    countPaymentTransactions: builder.query<number, { where?: PaymentTransactionsFilter }>({
      queryFn: async ({ where = {} }) => {
        const { metaOriginalTransactionId, subscriptionDocId, ...rest } = where as any;
        const whereFirestore: Record<string, any> = { ...rest };
        if (metaOriginalTransactionId) {
          whereFirestore["meta.originalTransactionId"] = metaOriginalTransactionId;
        }
        if (subscriptionDocId) {
          whereFirestore["subscriptionDocId"] = subscriptionDocId;
        }
        // Helper to check if a transaction matches non-id filters
        const matchesFilters = (t: PaymentTransaction, filters: Partial<PaymentTransactionsFilter>): boolean => {
          const { transactionId: _ignored, ...rest } = filters;
          const entries = Object.entries(rest) as [keyof typeof rest, any][];
          return entries.every(([key, value]) => (value == null ? true : (t as any)[key] === value));
        };

        if (where.transactionId) {
          const { transactionId, ...restFilters } = where;
          let transaction: PaymentTransaction | null = null;
          try {
            transaction = (await paymentTransactionRepository.findById(transactionId as string)) as PaymentTransaction | null;
          } catch (e) {
            return { error: (e as Error).message || "Failed to fetch payment transaction" } as any;
          }
          // Return 1 only if the transaction exists and matches other filters; else 0
          return { data: transaction && matchesFilters(transaction, restFilters) ? 1 : 0 } as any;
        }
        return executeQuery(() => paymentTransactionRepository.count(whereFirestore as any));
      },
    }),
  }),
});

export const { useGetPaymentTransactionsQuery, useCountPaymentTransactionsQuery, useGetPaymentTransactionByIdQuery } = paymentTransactionsService;
